// palette.wgsl — maps iteration counts (and, for Newton, a converged root)
// to pixel colors. Concatenated ahead of every formula shader alongside
// complex.wgsl (see src/util/pipeline.mjs).

// Escape-time coloring shared by every Mandelbrot-type/Julia-type shader.
// Packs a color as 0xAABBGGRR — little-endian byte order [R, G, B, A],
// which is exactly what Uint8ClampedArray/ImageData expects (see
// src/util/pixels.mjs), so the readback buffer needs no repacking.
fn colorize(iter: u32, max_iter: u32, z: Complex) -> u32 {
  if (iter >= max_iter) {
    return 0xff000000u; // opaque black — point never escaped
  }

  // Smooth (fractional) iteration count from the escape magnitude, so bands
  // between integer iteration counts blend instead of producing hard rings.
  let log_zn = log(cAbs2(z)) * 0.5;
  let nu = log(log_zn / log(2.0)) / log(2.0);
  let smooth_iter = f32(iter) + 1.0 - nu;
  let t = clamp(smooth_iter / f32(max_iter), 0.0, 1.0);

  let hue = fract(t * 3.0 + 0.6) * 6.0;
  let x = 1.0 - abs(fract(hue * 0.5) * 2.0 - 1.0);
  var rgb: vec3<f32>;
  if      (hue < 1.0) { rgb = vec3<f32>(1.0, x, 0.0); }
  else if (hue < 2.0) { rgb = vec3<f32>(x, 1.0, 0.0); }
  else if (hue < 3.0) { rgb = vec3<f32>(0.0, 1.0, x); }
  else if (hue < 4.0) { rgb = vec3<f32>(0.0, x, 1.0); }
  else if (hue < 5.0) { rgb = vec3<f32>(x, 0.0, 1.0); }
  else                 { rgb = vec3<f32>(1.0, 0.0, x); }

  return packRgb(rgb);
}

// Basin-of-attraction coloring for Newton's method: one hue per root, shaded
// darker the more iterations that pixel took to converge.
fn colorizeRoot(root_index: u32, iter: u32, max_iter: u32) -> u32 {
  if (root_index >= 3u) {
    return 0xff000000u; // never converged within max_iter
  }
  var rgb: vec3<f32>;
  switch root_index {
    case 0u:  { rgb = vec3<f32>(1.0, 0.25, 0.25); }
    case 1u:  { rgb = vec3<f32>(0.25, 1.0, 0.35); }
    default:  { rgb = vec3<f32>(0.3, 0.45, 1.0); }
  }
  let shade = clamp(1.0 - f32(iter) / f32(max_iter) * 1.5, 0.25, 1.0);
  return packRgb(rgb * shade);
}

fn packRgb(rgb: vec3<f32>) -> u32 {
  let r = u32(clamp(rgb.r, 0.0, 1.0) * 255.0);
  let g = u32(clamp(rgb.g, 0.0, 1.0) * 255.0);
  let b = u32(clamp(rgb.b, 0.0, 1.0) * 255.0);
  return 0xff000000u | (b << 16u) | (g << 8u) | r;
}
